import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";
import { useRosterAndStaffQueryDataUpdater } from "./useRosterAndStaffQueryDataUpdater";

type Props = {
  entityId: string;
  seasonId: string;
};

export const useFetchSeasonRoster = ({ entityId, seasonId }: Props) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["seasonRoster", sport, organizationId, entityId, seasonId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster`, {
        defaultErrorMessage: "Can't fetch season roster",
      }),
  });
};

export const useFetchPublishedSeasonRoster = ({ entityId, seasonId, enabled }: Props & { enabled: boolean }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["seasonRoster", "published", sport, organizationId, entityId, seasonId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster/published`, {
        defaultErrorMessage: "Can't fetch published season roster",
      }),
    enabled,
  });
};

export const useDeleteSeasonRosterPlayers = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { deleteRecords } = useRosterAndStaffQueryDataUpdater();

  return useMutation({
    mutationFn: ({ entityId, seasonId, data }: Props & { data: any }) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster`, {
        method: "DELETE",
        body: data,
      });
    },
    onSuccess: (response: any, { entityId, seasonId, data }: Props & { data: any }) => {
      const result = deleteRecords(
        ["seasonRoster", sport, organizationId, entityId, seasonId],
        response,
        data?.personIds,
        data?.dcPersonIds
      );
      if (result) {
        queryClient.invalidateQueries({
          queryKey: ["season", sport, organizationId, entityId, seasonId],
        });
        showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.deleted" }));
      }
    },
  });
};

export const useAddSeasonRosterPlayers = () => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { addRecords } = useRosterAndStaffQueryDataUpdater();

  return useMutation({
    mutationFn: ({ entityId, seasonId, data }: Props & { data: any }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster`, {
        method: "POST",
        body: data,
      }),
    onSuccess: (response: any, { entityId, seasonId }: Props & { data: any }) => {
      const result = addRecords(["seasonRoster", sport, organizationId, entityId, seasonId], response);
      if (result) {
        queryClient.invalidateQueries({
          queryKey: ["season", sport, organizationId, entityId, seasonId],
        });
        showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.added" }));
      }
    },
  });
};

export const useUpdateSeasonRosterPlayerInternal = () => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();

  return useMutation({
    mutationFn: ({ entityId, seasonId, personId, data }: Props & { personId: string; data: any }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster/${personId}`, {
        method: "PUT",
        body: data,
      }),
    onSuccess: (response: any, { entityId, seasonId }: Props & { personId: string; data: any }) => {
      replaceRecords(["seasonRoster", sport, organizationId, entityId, seasonId], response);
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
  });
};

export const useUpdateSeasonRosterPlayerDc = () => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();

  return useMutation({
    mutationFn: ({ entityId, seasonId, dcPersonId, data }: Props & { dcPersonId: string; data: any }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/roster/dc/${dcPersonId}`, {
        method: "PUT",
        body: data,
      }),
    onSuccess: (response: any, { entityId, seasonId }: Props & { dcPersonId: string; data: any }) => {
      replaceRecords(["seasonRoster", sport, organizationId, entityId, seasonId], response);
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
  });
};
